'use client';

import React, {
  type CSSProperties,
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
} from 'react';
import { useMinLoadingState } from 'components/DropZone/useMinLoadingState';
import { useReducedMotion } from 'framer-motion';
import styled from 'styled-components';
import { theme } from '@frameio/vapor-theme';
import type { ToastLoadingProps, ToastSizes } from './ToastTypes';

export type ToastDisplayProps = Required<
  PropsWithChildren<
    ToastLoadingProps &
      ToastSizes & {
        /**
         * Reference to the toast element -- needed to animate the toast
         */
        toastRef: React.RefObject<HTMLDivElement>;
      }
  >
>;

/**
 * Determines the size of the toast and animates the toast when the loading state changes
 */
export function ToastAnimationDisplay({
  children,
  isLoading: isLoadingProp,
  size,
  toastRef,
}: ToastDisplayProps) {
  const isLoading = useMinLoadingState(isLoadingProp, 750);
  const existingSize = useRef<{ width: number; height: number } | null>(null);
  const reducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const node = toastRef.current;
    if (!node) {
      return;
    }
    const parent = node.parentElement;
    if (!parent) {
      return;
    }
    const shouldAnimate = existingSize.current !== null;

    // determine the size of the child node given a non-defined
    // width of the parent
    parent.style.width = 'auto';
    const rect = node.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    // set the width and height of the child with absolute
    // units to prevent resizing during animations
    const existingW = node.style.width;
    const existingH = node.style.height;

    node.style.width = `${w}px`;
    node.style.height = `${h}px`;

    // After the animation has run, restore the parent's width and height
    // to 'auto' and the child node to its original size. This
    // enables the toast to grow and shrink as needed when
    // the viewport changes size.
    function setParentStyle(w: number, h: number) {
      if (!node || !parent || !existingSize) {
        return;
      }
      if (isLoading) {
        // the loading toast renders using absolute positioning
        // to overlay the existing toast. We need to ensure the parent
        // has a defined width and height so that the
        // toast parent renders the proper size.
        parent.style.width = `${w}px`;
        parent.style.height = `${h}px`;
      } else {
        parent.style.width = 'auto';
        parent.style.height = 'auto';
      }

      node.style.width = existingW;
      node.style.height = existingH;
    }

    if (shouldAnimate) {
      parent.animate(
        [
          {
            width: `${existingSize.current?.width}px`,
            height: `${existingSize.current?.height}px`,
          },
          { width: `${w}px`, height: `${h}px` },
        ],
        {
          duration: reducedMotion ? 0 : 450,
          easing: 'ease',
        }
      ).onfinish = () => {
        setParentStyle(w, h);
      };
    } else {
      setParentStyle(w, h);
    }

    // record the size of the child node so that we can
    // animate from that size if our loading state changes
    if (w > 0 && h > 0) {
      existingSize.current = { width: w, height: h };
    }
  }, [isLoading, reducedMotion, toastRef]);

  const styles = getToastStylesFromSize(size);

  return (
    <StyledToast role="status" style={styles}>
      {children}
    </StyledToast>
  );
}

const StyledToast = styled.div`
  background: ${theme.colors.components.toast.background};
  border-radius: var(--radius);
  box-shadow: 0 0 0 1px ${theme.colors.border.primary} inset,
    ${theme.shadows['12dp']};
  overflow: hidden;
  position: relative;
  user-select: none;
  will-change: height, width;
`;

/**
 * determine the css variables for the toast based on the size
 * @param size
 */
function getToastStylesFromSize(size: 'default' | 'small'): CSSProperties {
  const baseStyles = {
    '--horizontal-padding': theme.spaces[8],
    '--min-height': theme.spaces[48],
    '--vertical-padding': theme.spaces[8],
    '--radius': theme.radii[8],
  } as CSSProperties;

  return size === 'default'
    ? ({
        ...baseStyles,
        '--min-height': '56px',
        '--vertical-padding': theme.spaces[12],
        '--horizontal-padding': theme.spaces[12],
      } as CSSProperties)
    : baseStyles;
}
